import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  Portal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { Fragment, useCallback, useMemo } from "react";
import { CombinedWarehouseOrderItemDto, OrderLineItemDto, ProductType } from "@lib/ShiOneClient";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import NoProductImage from "@images/no-product-image-available.svg";
import useBuyAgainOrder from "@features/ecommerce/hooks/useBuyAgainOrder";
import BuyAgainResultDialog from "@features/ecommerce/components/BuyAgainResultDialog";
import useBuyAgainResultDialog from "@features/ecommerce/components/useBuyAgainResultDialog";
import { useOrdersCartHandoff } from "@features/ecommerce/cart/useOrdersCartHandoff";
import ProductCustomFields from "./ProductCustomFields";
import { useGetOrderLineRelatedData } from "@features/orders-features/hooks/useGetOrderLineRelatedData";
import { useDrawerStack } from "shared-ui";
import { StackedIntegrationCenterTransferIntegrationDrawer } from "@features/orders-features/components/order-drawer/StackedIntegrationCenterTransferIntegrationDrawer";
import { useOrderTelemetrySection } from "../../orderTelemetrySection";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { INTEGRATION_TRANSFER_DRAWER_SEGMENT } from "../../orderDrawerSegments";
import { toIntegrationTransferRouteId } from "../../integrationTransferRouteId";

const PRODUCT_IMAGE_WIDTH = 71.25;
const PRODUCT_IMAGE_HEIGHT = 60;
const PRODUCT_IMAGE_TEXT_GAP = 2;

interface ShipmentProductTableProps {
  items: OrderLineItemDto[];
  currencyCode?: string | null;
  onProductClick?: (item: OrderLineItemDto) => void;
  biOrderId?: number | null;
  /** Warehouse pick-ticket items — when provided, renders per-item pick ticket rows (processing section only). */
  myWarehouseOrders?: CombinedWarehouseOrderItemDto[] | null;
  /** Returns true when the given product is on backorder (processing section only). */
  isBackorder?: (
    productDescriptor: string | undefined,
    productClass: string | undefined,
  ) => boolean;
}

function getItemKey(item: OrderLineItemDto, index: number): string {
  return (
    item.lineItemId ??
    `${item.mfgPartNumber ?? item.shiPartNumber ?? "item"}-${item.lineNumber ?? index}`
  );
}



export function ShipmentProductTable({
  items,
  currencyCode,
  onProductClick,
  biOrderId,
  myWarehouseOrders,
  isBackorder,
}: Readonly<ShipmentProductTableProps>) {
  const telemetrySection = useOrderTelemetrySection();
  const formatter = useMemo(() => createSavedCurrencyFormatter(currencyCode), [currencyCode]);
  const {
    mutate: buyAgain,
    isPending: isAddingToCart,
    clearBuyAgainResult,
  } = useBuyAgainOrder();
  const {
    canSaveToCart,
    handoffBuyAgainSuccess,
  } = useOrdersCartHandoff();
  const { pushDrawer } = useDrawerStack();
  const { show: showLocalResultDialog, dialogProps: resultDialogProps } =
    useBuyAgainResultDialog(clearBuyAgainResult);

  const {
    data: relatedData,
    isLoading: isLoadingRelatedData,
    isError: isErrorRelatedData,
  } = useGetOrderLineRelatedData(biOrderId ?? undefined);

  const getRelatedData = useCallback(
    (lineNumber: number | undefined) => {
      if (
        !lineNumber ||
        isLoadingRelatedData ||
        isErrorRelatedData ||
        !relatedData
      ) {
        return undefined;
      }
      return relatedData.filter(
        (p: NonNullable<typeof relatedData>[number]) =>
          !!p.lineNumber && p.lineNumber === lineNumber,
      );
    },
    [isLoadingRelatedData, isErrorRelatedData, relatedData],
  );

  const handlePickTicketClick = useCallback(
    (ticket: CombinedWarehouseOrderItemDto) => {
      const routeId = toIntegrationTransferRouteId(ticket);
      pushDrawer({
        id: `integration-transfer-${ticket.uniqueIdentifier}`,
        component: StackedIntegrationCenterTransferIntegrationDrawer,
        props: { details: ticket },
        ...(routeId && {
          route: { segment: INTEGRATION_TRANSFER_DRAWER_SEGMENT, id: routeId },
        }),
      });
    },
    [pushDrawer],
  );

  const handleAddToCart = useCallback(
    (item: OrderLineItemDto) => {
      buyAgain([item], {
        onSuccess: (result) => {
          const outcome = handoffBuyAgainSuccess(result);
          if (outcome === "stayed" && result.failedCount === 0) {
            return;
          }

          if (
            outcome === "no-cart-view" &&
            result.successCount > 0 &&
            result.failedCount === 0
          ) {
            return;
          }

          showLocalResultDialog(result);
        },
      });
    },
    [buyAgain, handoffBuyAgainSuccess, showLocalResultDialog],
  );

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
        No products found.
      </Typography>
    );
  }

  return (
    <Table
      size="small"
      sx={{
        tableLayout: "fixed",
        width: "100%",
        "& td, & th": { border: 0 },
        "& thead th": {
          borderBottom: "1px solid",
          borderColor: "divider",
          whiteSpace: "nowrap",
        },
      }}
    >
      <TableHead>
        <TableRow sx={{ bgcolor: "action.hover" }}>
          <TableCell sx={{ px: 1, py: 1 }}>Product</TableCell>
          <TableCell align="right" sx={{ px: 1, py: 1, width: 104 }}>
            Unit Price
          </TableCell>
          <TableCell align="right" sx={{ px: 1, py: 1, width: 64 }}>
            Qty
          </TableCell>
          <TableCell align="right" sx={{ px: 1, py: 1, width: 104 }}>
            Total
          </TableCell>
          <TableCell align="left" sx={{ px: 1, py: 1, width: 72 }}>
            Actions
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {items.map((item, index) => {
          // In a per-shipment context show the quantity shipped in THIS shipment,
          // not the full ordered quantity (which repeats across every shipment row).
          const displayQuantity = item.qtyShipped ?? item.quantity ?? 0;
          const total =
            item.qtyShipped != null
              ? (item.unitPrice ?? 0) * item.qtyShipped
              : (item.extendedPrice ?? (item.unitPrice ?? 0) * (item.quantity ?? 0));
          const relatedFields = getRelatedData(item.lineNumber ?? undefined);
          const showAddToCart = item.productType !== ProductType.FEES;

          return (
            <Fragment key={getItemKey(item, index)}>
              <TableRow>
                <TableCell sx={{ px: 1, py: 1.5 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `${PRODUCT_IMAGE_WIDTH}px minmax(0, 1fr)`,
                      columnGap: PRODUCT_IMAGE_TEXT_GAP,
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={item.imageUrl ?? NoProductImage}
                      alt={
                        item.productName ??
                        item.mfgPartNumber ??
                        "Unknown Product"
                      }
                      sx={{
                        width: PRODUCT_IMAGE_WIDTH,
                        height: PRODUCT_IMAGE_HEIGHT,
                        bgcolor: "transparent",
                        overflow: "hidden",
                        flexShrink: 0,
                        "& .MuiAvatar-img": {
                          objectFit: "contain",
                          objectPosition: "center",
                        },
                      }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Button
                        disabled={!onProductClick}
                        onClick={() => onProductClick?.(item)}
                        size="small"
                        variant="text"
                        color="primary"
                        sx={{
                          p: 0,
                          minWidth: 0,
                          textTransform: "none",
                          justifyContent: "flex-start",
                          width: "100%",
                        }}
                        {...telemetryProps({
                          name: "orders_product_open",
                          section: telemetrySection,
                          label: "Open product details",
                          ...((item.productDescriptor ?? item.mfgPartNumber)
                            ? {
                                id: String(
                                  item.productDescriptor ?? item.mfgPartNumber,
                                ),
                              }
                            : {}),
                        })}
                      >
                        <Tooltip title={item.productName}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "primary.main",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: onProductClick ? "pointer" : "default",
                              "&:hover": onProductClick
                                ? { textDecoration: "underline" }
                                : undefined,
                            }}
                          >
                            {item.productName}
                          </Typography>
                        </Tooltip>
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Mfr #: {item.mfgPartNumber ?? "—"} SHI #:{" "}
                        {item.shiPartNumber ?? "—"}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ px: 1, py: 1.5, whiteSpace: "nowrap" }}
                >
                  <Typography variant="body2" color="textPrimary">
                    {formatter.format(item.unitPrice ?? 0)}
                  </Typography>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ px: 1, py: 1.5, whiteSpace: "nowrap" }}
                >
                  <Typography variant="body2" color="textPrimary">
                    {displayQuantity}
                  </Typography>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ px: 1, py: 1.5, whiteSpace: "nowrap" }}
                >
                  <Typography variant="body2" color="textPrimary">
                    {formatter.format(total)}
                  </Typography>
                </TableCell>

                <TableCell align="left" sx={{ px: 1, py: 1.5 }}>
                  {showAddToCart && (
                    <Tooltip title="Add to cart">
                      <span>
                        <IconButton
                          size="small"
                          color="inherit"
                          disabled={
                            isAddingToCart ||
                            !canSaveToCart
                          }
                          onClick={() => handleAddToCart(item)}
                          aria-label={`Add ${item.productName ?? "product"} to cart`}
                          {...telemetryProps({
                            name: "orders_add_to_cart",
                            section: telemetrySection,
                            label: "Add to cart",
                            ...((item.productDescriptor ?? item.mfgPartNumber)
                              ? {
                                  id: String(
                                    item.productDescriptor ?? item.mfgPartNumber,
                                  ),
                                }
                              : {}),
                          })}
                        >
                          <AddShoppingCartIcon fontSize="small" color="action" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>

              <ProductCustomFields
                xWidth={PRODUCT_IMAGE_WIDTH}
                xGap={PRODUCT_IMAGE_TEXT_GAP}
                lineNumber={item.lineNumber ?? undefined}
                relatedData={relatedFields ?? undefined}
                colSpan={5}
              />

              {(() => {
                const backorder = isBackorder
                  ? isBackorder(item.productDescriptor, item.productClass)
                  : false;
                const pickTickets = (myWarehouseOrders ?? []).filter(
                  (wo) =>
                    !!wo.uniqueIdentifier &&
                    item.lineNumber != null &&
                    wo.lineNum === item.lineNumber,
                );
                if (!backorder && pickTickets.length === 0) return null;
                return (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{
                        py: 0,
                        pr: 1,
                        pb: 1,
                        border: 0,
                        // Align alert with the text column: base cell padding (8px)
                        // + image width (71.25px) + grid column-gap (spacing(2) = 16px).
                        pl: `${8 + PRODUCT_IMAGE_WIDTH + PRODUCT_IMAGE_TEXT_GAP * 8}px`,
                      }}
                    >
                      <Stack spacing={0.5}>
                        {backorder && (
                          <Alert severity="error" sx={{ py: 0.5 }}>
                            This item is currently on backorder. It will ship
                            once it is in stock.
                          </Alert>
                        )}
                        {pickTickets.map((ticket) => (
                          <Alert severity="info" key={ticket.uniqueIdentifier} sx={{ py: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography variant="body2">
                                This item is being worked on in our Integration Center.
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handlePickTicketClick(ticket)}
                                sx={{
                                  p: 0,
                                  minWidth: 0,
                                  textTransform: "none",
                                  fontSize: "body2.fontSize",
                                  lineHeight: "inherit",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                                {...telemetryProps({
                                  name: "orders_open_pick_ticket",
                                  section: telemetrySection,
                                  label: "Open pick ticket",
                                  ...(ticket.uniqueIdentifier
                                    ? { id: String(ticket.uniqueIdentifier) }
                                    : {}),
                                })}
                              >
                                {ticket.uniqueIdentifier}
                              </Button>
                              {ticket.orderItemQty != null && (
                                <Typography variant="body2" color="text.secondary">
                                  (Qty {ticket.orderItemQty})
                                </Typography>
                              )}
                            </Box>
                          </Alert>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })()}
            </Fragment>
          );
        })}
      </TableBody>
      <Portal>
        <BuyAgainResultDialog {...resultDialogProps} />
      </Portal>
    </Table>
  );
}
